import { ApplicationRoleCode } from '@kcftech/prometheus-client';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useAccess } from 'contexts/AccessContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { useUser } from 'contexts/UserContext';
import { NotFound } from 'pages/NotFound';
import { ReactNode } from 'react';
import { isUserInternal } from 'utils/RouteHelper';

export type PremiumContentType = Readonly<{
    children?: ReactNode;
    /** if True, route will always be accessible to SaaS */
    allowSaas?: boolean;
    /** if True, route will always be accessible to SaaS/Sales/Sentry */
    allowKCF?: boolean;
}>;

/**
 * Restricts the content to only be viewable if the route Corporation has premium access.
 * * If the Corporation is loading, a spinner is displayed.
 * * If the loaded Corporation does not have premium access, `<NotFound>` is displayed.
 */
export function PremiumContent({
    children,
    allowSaas = false,
    allowKCF = false,
}: PremiumContentType) {
    const { isLoading, corporation } = useRouteEntities();
    const { user } = useUser();
    const { isPremium } = useAccess();

    const saasAlways =
        allowSaas && user?.ApplicationRole === ApplicationRoleCode.GlobalAdmin;
    const kcfAlways = allowKCF && isUserInternal(user?.ApplicationRole);

    // Wait for Corporation route entity to load before defining routes to prevent
    // the flashing of the Not Found page during loading.
    if (isLoading && !corporation) {
        return <SpinnerArea absolute />;
    }

    if (!isPremium && !saasAlways && !kcfAlways) {
        return <NotFound />;
    }

    return <>{children}</>;
}
